<!-- frontend/src/views/MyActivities.vue -->
<template>
  <div class="my-activities">
    <h2>我的活動</h2>
    
    <!-- 標籤頁導航 -->
    <div class="tabs">
      <button 
        @click="activeTab = 'registered'" 
        :class="['tab-btn', { active: activeTab === 'registered' }]"
      >
        報名查詢/取消報名
      </button>
      <button 
        @click="activeTab = 'history'" 
        :class="['tab-btn', { active: activeTab === 'history' }]"
      >
        學習歷程與成果追蹤
      </button>
    </div>
    
    <!-- 報名查詢/取消報名標籤頁 -->
    <div v-if="activeTab === 'registered'" class="tab-content">
      <!-- 預計可得總點數 -->
      <div class="total-points">
        <h3>預計可得總點數：<span class="points-number">{{ totalExpectedPoints }}</span> 點</h3>
      </div>
      
      <!-- Loading 狀態 -->
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>
      
      <!-- 無活動 -->
      <div v-else-if="recentRegistered.length === 0" class="no-data">
        <p>您近 4 個月內尚無報名任何活動</p>
        <router-link to="/search" class="browse-btn">瀏覽活動</router-link>
      </div>

      <!-- 已報名活動表格（近 4 個月） -->
      <div v-else class="table-container">
        <table class="activities-table">
          <thead>
            <tr>
              <th>序號</th>
              <th>活動名稱</th>
              <th>日期</th>
              <th>時間</th>
              <th>地點</th>
              <th>預計點數</th>
              <th>備註</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(activity, index) in recentRegistered" :key="activity.id">
              <td>{{ index + 1 }}</td>
              <td class="activity-title">{{ activity.title }}</td>
              <td>{{ formatDateRange(activity) }}</td>
              <td>{{ formatTimeRange(activity) }}</td>
              <td>{{ activity.location || '待定' }}</td>
              <td class="points">{{ calculatePoints(activity.learning_hours) }} 點</td>
              <td class="remarks">{{ getRemarks(activity) }}</td>
              <td>
                <button
                  v-if="!hasStarted(activity)"
                  @click="confirmCancelRegistration(activity)"
                  class="cancel-btn"
                  :disabled="cancellingId === activity.id"
                >
                  {{ cancellingId === activity.id ? '取消中...' : '取消報名' }}
                </button>
                <span v-else class="locked-text" title="活動已開始，無法取消報名">活動已開始</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- 成功/錯誤訊息 -->
      <div v-if="message" :class="['message', message.type]">
        {{ message.text }}
      </div>
    </div>
    
    <!-- 學習歷程與成果追蹤標籤頁 -->
    <div v-if="activeTab === 'history'" class="tab-content history-tab">
      <!-- 1. 上層彙總表：學籍期間總覽 + 圓餅圖 -->
      <div class="summary-section">
        <h3>學習歷程與成果追蹤總覽<span class="sub-title">（學籍期間累計）</span></h3>

        <div class="summary-layout">
          <!-- 圓餅圖 -->
          <div class="pie-chart-block">
            <h4>各類型點數分佈</h4>
            <div class="pie-chart">
              <svg viewBox="0 0 100 100" class="donut-chart">
                <circle cx="50" cy="50" r="30" fill="none" stroke="#f0f0f0" stroke-width="20" />
                <circle
                  v-for="(slice, idx) in pieChartSlices"
                  :key="idx"
                  :r="30"
                  :cx="50"
                  :cy="50"
                  :stroke="slice.color"
                  :stroke-width="20"
                  :stroke-dasharray="`${slice.length} ${slice.gap}`"
                  :stroke-dashoffset="-slice.offset"
                  fill="none"
                />
                <text x="50" y="48" text-anchor="middle" class="pie-center-label">總點數</text>
                <text x="50" y="58" text-anchor="middle" class="pie-center-value">{{ totalPoints }}</text>
              </svg>
              <div class="pie-legend">
                <div v-for="cat in categoryTotals" :key="cat.name" class="legend-item">
                  <span class="legend-color" :style="{ backgroundColor: cat.color }"></span>
                  <span class="legend-label">{{ cat.name }}</span>
                  <span class="legend-value">{{ cat.points }} 點（{{ cat.percentage }}%）</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 彙總表 -->
          <div class="summary-table-wrapper">
            <table class="summary-table">
              <thead>
                <tr>
                  <th>類別</th>
                  <th>子項目</th>
                  <th>學分名稱</th>
                  <th>學分上限</th>
                  <th>已獲得點數</th>
                  <th>未兌換點數</th>
                  <th>已兌換學分</th>
                  <th>累計參與活動數</th>
                  <th>備註</th>
                </tr>
              </thead>
              <tbody>
                <template v-for="cat in summaryRows" :key="cat.name">
                  <tr
                    v-for="(sub, subIdx) in cat.subcategories"
                    :key="cat.name + '-' + sub.name"
                    :class="{ 'category-row-first': subIdx === 0 }"
                  >
                    <td
                      v-if="subIdx === 0"
                      :rowspan="cat.subcategories.length"
                      class="category-cell"
                      :style="{ borderLeft: '4px solid ' + cat.color }"
                    >
                      {{ cat.name }}
                    </td>
                    <td class="subcategory-cell">{{ sub.name }}</td>
                    <td>{{ sub.creditName || '—' }}</td>
                    <td>{{ sub.creditLimit || '—' }}</td>
                    <td class="num-cell points-cell">{{ sub.earnedPoints }} 點</td>
                    <td class="num-cell">{{ sub.unexchangedPoints }} 點</td>
                    <td class="num-cell credits-cell">{{ sub.exchangedCredits }} 學分</td>
                    <td class="num-cell">{{ sub.activityCount }} 場</td>
                    <td class="note-cell">{{ sub.note || '—' }}</td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 2. 下層明細表：個人活動紀錄（與報名查詢連動） -->
      <div class="records-section">
        <div class="records-header">
          <h3>個人活動紀錄</h3>
        </div>

        <div class="filters-row">
          <div class="filter-group">
            <label>日期區間：</label>
            <input type="date" v-model="dateFrom" class="filter-date" />
            <span class="date-separator">~</span>
            <input type="date" v-model="dateTo" class="filter-date" />
          </div>
          <button class="filter-reset" @click="resetFilters">重置篩選</button>
        </div>

        <div class="table-container">
          <table class="records-table">
            <thead>
              <tr>
                <th>活動日期</th>
                <th>活動名稱</th>
                <th>活動類型</th>
                <th>點數</th>
                <th>學分</th>
                <th>心得狀態</th>
                <th>兌換學分學期與日期</th>
                <th>備註</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="record in filteredRecords" :key="record.id">
                <td>{{ formatDate(record.date) }}</td>
                <td class="activity-name">{{ record.name }}</td>
                <td>{{ record.type }}</td>
                <td class="points-cell">{{ record.points > 0 ? record.points : '—' }}</td>
                <td class="dev-cell">功能開發中</td>
                <td>
                  <span class="essay-status" :class="getEssayStatusClass(record.essayStatus)">
                    {{ getEssayStatusText(record.essayStatus) }}
                  </span>
                </td>
                <td class="dev-cell">功能開發中</td>
                <td class="note-cell">{{ record.note || '—' }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="filteredRecords.length === 0" class="no-records">
            沒有符合條件的活動紀錄
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getUserActivities, cancelRegistration, getMyPoints } from '@/api.js'
import { categoryLabel as catLabel } from '@/utils/category.js'

export default {
  name: 'MyActivities',
  data() {
    return {
      activeTab: 'registered',
      registeredActivities: [],
      loading: false,
      cancellingId: null,
      message: null,

      // 明細表篩選條件
      dateFrom: '',
      dateTo: '',
      selectedSubcategory: '',

      // 四大類別結構定義（來源：自主學習系統待修改畫面.docx 第 1~2 頁）
      categoryTree: [
        {
          name: '通識微型課程',
          color: '#4f8ef7',
          subcategories: [
            { name: '學生募課', creditName: '通識學習拼圖(一)～(三)', creditLimit: '3學分', note: '修課紀錄由通識中心統一審查' },
            { name: '微型課程', creditName: '', creditLimit: '', note: '由教務資訊系統-微型課程-學生時數統計匯入；提供連結供學生自行查看' }
          ]
        },
        {
          name: '興通識online',
          color: '#8b5cf6',
          subcategories: [
            { name: '興通識online', creditName: '興通識online(一)~(四)', creditLimit: '4學分', note: '由教務資訊系統-興通識online-學生點數與學分統計匯入；提供連結供學生自行查看' }
          ]
        },
        {
          name: '實作專題',
          color: '#22c55e',
          subcategories: [
            { name: '實作專題', creditName: '通識專題製作(一)、(二)', creditLimit: '2學分', note: '須通過成果審查' }
          ]
        },
        {
          name: '跨域自主學習活動',
          color: '#f59e0b',
          subcategories: [
            { name: '惠蓀講座', creditName: '跨域自主學習(一)、(二)', creditLimit: '2學分', note: '需上傳心得（非強制，上傳心得可額外給點數）' },
            { name: '其他活動', creditName: '', creditLimit: '', note: '依活動類型給點' }
          ]
        },
        {
          name: '生活實踐',
          color: '#ef4444',
          subcategories: [
            { name: '工作學習', creditName: '跨域興學習(一)、(二)', creditLimit: '2學分', note: '依活動類型給點' },
            { name: '兵役訓練', creditName: '', creditLimit: '', note: '依活動類型給點' },
            { name: '競賽成果', creditName: '', creditLimit: '', note: '需上傳心得與證明文件' },
            { name: '職業證照', creditName: '', creditLimit: '', note: '需上傳證明文件' },
            { name: '遊歷學習', creditName: '', creditLimit: '', note: '需上傳心得與證明文件' },
            { name: '志工服務', creditName: '', creditLimit: '', note: '依活動類型給點' }
          ]
        }
      ],

      // 使用者實際點數（來自 /api/my-points；惠蓀講座由舊系統同步，依使用者動態顯示）
      userPoints: { summary: [], records: [] },

    }
  },
  computed: {
    // 報名查詢/取消報名：只列出近 4 個月（含未來）有報名的活動
    recentRegistered() {
      const cutoff = new Date()
      cutoff.setMonth(cutoff.getMonth() - 4)
      return this.registeredActivities.filter(a => {
        const s = a.start_date || a.activity_date
        if (!s) return true
        const d = new Date(s)
        return isNaN(d.getTime()) ? true : d >= cutoff
      })
    },

    totalExpectedPoints() {
      return this.recentRegistered.reduce((total, activity) => {
        return total + this.calculatePoints(activity.learning_hours)
      }, 0)
    },

    // 當學期起迄（以今日為基準；2-7 月算春季學期，8-1 月算秋季學期）
    currentSemester() {
      const today = new Date()
      const month = today.getMonth() + 1
      const year = today.getFullYear()
      const rocYear = year - 1911
      if (month >= 8) {
        return { label: `${rocYear}-1`, start: `${year}-08-01`, end: `${year + 1}-01-31` }
      } else if (month <= 1) {
        return { label: `${rocYear - 1}-1`, start: `${year - 1}-08-01`, end: `${year}-01-31` }
      } else {
        return { label: `${rocYear - 1}-2`, start: `${year}-02-01`, end: `${year}-07-31` }
      }
    },

    currentSemesterLabel() {
      return this.currentSemester.label
    },

    // 上層彙總表：只顯示真實點數（/api/my-points）；無資料者顯示 0
    summaryRows() {
      const real = {}
      this.userPoints.summary.forEach(s => {
        real[s.category + '|' + s.subcategory] = s
      })
      return this.categoryTree.map(cat => {
        const subcategories = cat.subcategories.map(sub => {
          const key = cat.name + '|' + sub.name
          const r = real[key]
          if (r) {
            return {
              ...sub,
              earnedPoints: r.earnedPoints,
              unexchangedPoints: r.earnedPoints,
              exchangedCredits: 0,
              activityCount: r.activityCount,
              isReal: true
            }
          }
          return { ...sub, earnedPoints: 0, unexchangedPoints: 0, exchangedCredits: 0, activityCount: 0, isReal: false }
        })
        return { ...cat, subcategories }
      })
    },

    // 圓餅圖：四大類別總點數
    categoryTotals() {
      const rows = this.summaryRows
      const total = rows.reduce(
        (sum, cat) => sum + cat.subcategories.reduce((s, sub) => s + sub.earnedPoints, 0),
        0
      )
      return rows.map(cat => {
        const points = cat.subcategories.reduce((s, sub) => s + sub.earnedPoints, 0)
        const percentage = total > 0 ? Math.round((points / total) * 100) : 0
        return { name: cat.name, points, percentage, color: cat.color }
      })
    },

    totalPoints() {
      return this.categoryTotals.reduce((s, c) => s + c.points, 0)
    },

    // 圓餅圖切片（周長 = 2 * π * 30 ≈ 188.5）
    pieChartSlices() {
      const CIRCUMFERENCE = 2 * Math.PI * 30
      const totals = this.categoryTotals.filter(c => c.points > 0)
      const sum = totals.reduce((s, c) => s + c.points, 0)
      if (sum === 0) return []
      let offset = 0
      return totals.map(cat => {
        const length = (cat.points / sum) * CIRCUMFERENCE
        const slice = {
          length,
          gap: CIRCUMFERENCE - length,
          color: cat.color,
          offset
        }
        offset += length
        return slice
      })
    },

    // 所有子項目（供下層表格篩選使用）
    allSubcategories() {
      const subs = []
      this.categoryTree.forEach(cat => {
        cat.subcategories.forEach(sub => subs.push(sub.name))
      })
      return subs
    },

    // 個人活動紀錄：與「報名查詢/取消報名」連動（近 4 個月報名的活動）
    personalRecords() {
      return this.recentRegistered.map(a => ({
        id: a.id,
        date: a.start_date || a.activity_date,
        name: a.title,
        type: this.categoryLabel(a.category),
        points: this.calculatePoints(a.learning_hours),
        essayStatus: null,
        note: this.getRemarks(a)
      }))
    },

    // 套用使用者日期區間（無限制學期）
    filteredRecords() {
      const fromDate = this.dateFrom
      const toDate = this.dateTo
      return this.personalRecords
        .filter(r => {
          if (!r.date) return true
          const d = String(r.date).slice(0, 10)
          if (fromDate && d < fromDate) return false
          if (toDate && d > toDate) return false
          return true
        })
        .sort((a, b) => String(a.date).localeCompare(String(b.date)))
    }
  },
  async created() {
    // 依網址 ?tab=history 切換到「學習歷程與成果追蹤」分頁
    if (this.$route.query.tab === 'history') {
      this.activeTab = 'history'
    }
    await this.loadRegisteredActivities()
    await this.loadMyPoints()
  },
  methods: {
    // 取得目前登入者的學習歷程點數（惠蓀講座為舊系統同步的真實資料）
    async loadMyPoints() {
      try {
        const res = await getMyPoints()
        if (res && res.data) {
          this.userPoints = {
            summary: res.data.summary || [],
            records: res.data.records || []
          }
        }
      } catch (e) {
        console.warn('載入學習點數失敗（顯示示範資料）:', e)
      }
    },

    async loadRegisteredActivities() {
      this.loading = true
      this.message = null
      
      try {
        const response = await getUserActivities()
        if (response && response.data) {
          this.registeredActivities = response.data
        }
      } catch (error) {
        console.error('載入活動失敗:', error)
        this.registeredActivities = []
      } finally {
        this.loading = false
      }
    },
    
    calculatePoints(learningHours) {
      // 預計點數 = 建立活動時填的自主學習點數（0~16 整數），直接對應、不設上限截斷
      const pts = parseInt(learningHours) || 0
      return Math.max(pts, 0)
    },

    // 活動是否已到開始時間（已開始者不可取消報名）
    hasStarted(activity) {
      const s = activity.start_date || activity.activity_date
      if (!s) return false
      const start = new Date(s)
      if (isNaN(start.getTime())) return false
      return Date.now() >= start.getTime()
    },

    categoryLabel(category) {
      return catLabel(category, '—')
    },
    
    formatDate(dateString) {
      if (!dateString) return '待定'

      try {
        const date = new Date(dateString)
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        return `${year}/${month}/${day}`
      } catch (error) {
        return '日期錯誤'
      }
    },

    formatDateRange(activity) {
      const startStr = activity.start_date || activity.activity_date
      const endStr = activity.end_date
      if (!startStr) return '待定'

      try {
        const start = new Date(startStr)
        const startDate = `${start.getFullYear()}/${String(start.getMonth() + 1).padStart(2, '0')}/${String(start.getDate()).padStart(2, '0')}`

        if (endStr) {
          const end = new Date(endStr)
          const endDate = `${end.getFullYear()}/${String(end.getMonth() + 1).padStart(2, '0')}/${String(end.getDate()).padStart(2, '0')}`
          // 同一天只顯示一個日期
          if (startDate === endDate) return startDate
          return `${startDate} ~ ${endDate}`
        }

        return startDate
      } catch (error) {
        return '日期錯誤'
      }
    },

    formatTimeRange(activity) {
      const startStr = activity.start_date || activity.activity_date
      const endStr = activity.end_date
      if (!startStr) return '待定'

      try {
        const start = new Date(startStr)
        const startTime = `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`

        if (endStr) {
          const end = new Date(endStr)
          const endTime = `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`
          return `${startTime} ~ ${endTime}`
        }

        return startTime
      } catch (error) {
        return '時間錯誤'
      }
    },
    
    getRemarks(activity) {
      const remarks = []

      if (activity.is_full) {
        remarks.push('活動已額滿')
      }

      if (activity.status === 'pending') {
        remarks.push('待審核')
      }

      // 使用 end_date 判斷活動是否已結束
      const endDateStr = activity.end_date
      if (endDateStr) {
        const endDate = new Date(endDateStr)
        const now = new Date()
        if (endDate < now) {
          remarks.push('活動已結束')
        }
      }

      return remarks.length > 0 ? remarks.join(', ') : '–'
    },
    
    confirmCancelRegistration(activity) {
      const pointsToLose = this.calculatePoints(activity.learning_hours)
      const confirmed = confirm(
        `您確定要取消報名此活動嗎？\n\n` +
        `活動名稱：${activity.title}\n` +
        `活動日期：${this.formatDate(activity.start_date || activity.activity_date)}\n` +
        `預計點數：${pointsToLose} 點\n\n` +
        `此操作無法復原。`
      )
      
      if (confirmed) {
        this.cancelRegistration(activity)
      }
    },
    
    async cancelRegistration(activity) {
      this.cancellingId = activity.id
      this.message = null
      
      try {
        await cancelRegistration(activity.id)
        
        const removedPoints = this.calculatePoints(activity.learning_hours)
        this.registeredActivities = this.registeredActivities.filter(
          a => a.id !== activity.id
        )
        
        this.showMessage(
          `取消報名成功！已從預計總點數扣除 ${removedPoints} 點。`,
          'success'
        )
        
      } catch (error) {
        console.error('取消報名失敗:', error)
        const errorMsg = error.response?.data?.detail || '系統錯誤'
        this.showMessage(`取消報名失敗：${errorMsg}`, 'error')
      } finally {
        this.cancellingId = null
      }
    },
    
    showMessage(text, type = 'info') {
      this.message = { text, type }
      setTimeout(() => {
        this.message = null
      }, 5000)
    },
    
    // 重置篩選條件
    resetFilters() {
      this.dateFrom = ''
      this.dateTo = ''
      this.selectedSubcategory = ''
    },

    // 將兌換日期轉成學期字串，例如 2025-11-22 → 114-1
    creditSemesterOf(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const month = d.getMonth() + 1
      const year = d.getFullYear()
      const rocYear = year - 1911
      if (month >= 8) return `${rocYear}-1`
      if (month <= 1) return `${rocYear - 1}-1`
      return `${rocYear - 1}-2`
    },

    // 取得心得狀態樣式
    getEssayStatusClass(status) {
      return {
        'status-none': status === 'none',
        'status-pending': status === 'pending',
        'status-reviewing': status === 'reviewing',
        'status-passed': status === 'passed'
      }
    },

    // 取得心得狀態文字
    getEssayStatusText(status) {
      const map = {
        none: '✕ 無需繳交',
        pending: '🔴 未提交',
        reviewing: '🟡 審核中',
        passed: '🟢 已通過'
      }
      return map[status] || '—'
    }
  }
}
</script>

<style scoped>
.my-activities {
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
}

h2 {
  color: #333;
  margin-bottom: 30px;
  text-align: center;
  font-size: 28px;
}

/* 標籤頁 */
.tabs {
  display: flex;
  border-bottom: 2px solid #e0e0e0;
  margin-bottom: 30px;
}

.tab-btn {
  flex: 1;
  padding: 15px 20px;
  background: none;
  border: none;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  border-bottom: 3px solid transparent;
  color: #666;
}

.tab-btn:hover {
  background-color: #f8f9fa;
}

.tab-btn.active {
  color: #007bff;
  font-weight: bold;
  border-bottom-color: #007bff;
}

.tab-content {
  min-height: 400px;
}

/* 預計總點數 */
.total-points {
  background: linear-gradient(135deg, #007bff, #0056b3);
  color: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  margin-bottom: 30px;
  box-shadow: 0 4px 8px rgba(0,123,255,0.2);
}

.total-points h3 {
  margin: 0;
  font-size: 24px;
}

.points-number {
  font-size: 32px;
  font-weight: bold;
  color: #ffd700;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.2);
}

/* 載入狀態 */
.loading {
  text-align: center;
  padding: 60px;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data {
  text-align: center;
  padding: 60px;
  color: #666;
  font-size: 18px;
}

.browse-btn {
  display: inline-block;
  margin-top: 20px;
  padding: 10px 30px;
  background-color: #007bff;
  color: white;
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.3s;
}

/* 表格容器 */
.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
  margin-bottom: 20px;
}

/* 活動表格 */
.activities-table {
  width: 100%;
  border-collapse: collapse;
}

.activities-table th {
  background-color: #007bff;
  color: white;
  padding: 15px 12px;
  text-align: left;
  font-weight: bold;
  font-size: 14px;
}

.activities-table td {
  padding: 15px 12px;
  border-bottom: 1px solid #eee;
  vertical-align: middle;
}

.activity-title {
  font-weight: bold;
  color: #333;
}

.points {
  font-weight: bold;
  color: #28a745;
  text-align: center;
}

.cancel-btn {
  background-color: #dc3545;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s;
}

.cancel-btn:hover:not(:disabled) {
  background-color: #c82333;
}

.cancel-btn:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.locked-text {
  color: #6c757d;
  font-size: 13px;
  font-style: italic;
  white-space: nowrap;
}

.dev-cell {
  text-align: center;
  color: #f57c00;
  font-size: 13px;
  font-style: italic;
  white-space: nowrap;
}

/* 訊息提示 */
.message {
  padding: 15px 20px;
  border-radius: 6px;
  margin-top: 20px;
  animation: slideIn 0.3s ease;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* 學習歷程標籤頁 */
.history-tab {
  padding: 20px 0;
}

.sub-title {
  font-size: 14px;
  color: #6c757d;
  font-weight: normal;
  margin-left: 8px;
}

/* 1. 上層彙總區（圓餅圖 + 彙總表） */
.summary-section {
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  margin-bottom: 30px;
}

.summary-section h3 {
  color: #333;
  margin-bottom: 20px;
  font-size: 20px;
}

.summary-layout {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 30px;
  align-items: start;
}

/* 圓餅圖區 */
.pie-chart-block h4 {
  color: #333;
  margin-bottom: 15px;
  font-size: 16px;
  text-align: center;
}

.pie-chart {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.donut-chart {
  width: 200px;
  height: 200px;
  transform: rotate(-90deg);
}

.pie-center-label {
  font-size: 7px;
  fill: #6c757d;
  transform: rotate(90deg);
  transform-origin: 50px 48px;
}

.pie-center-value {
  font-size: 10px;
  font-weight: bold;
  fill: #333;
  transform: rotate(90deg);
  transform-origin: 50px 58px;
}

.pie-legend {
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  font-size: 14px;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  flex-shrink: 0;
}

.legend-label {
  flex: 1;
  color: #495057;
}

.legend-value {
  color: #6c757d;
  font-weight: 600;
  font-size: 13px;
}

/* 彙總表 */
.summary-table-wrapper {
  overflow-x: auto;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  min-width: 800px;
}

.summary-table th {
  background-color: #f1f5f9;
  padding: 12px 8px;
  text-align: center;
  font-weight: 600;
  color: #334155;
  border: 1px solid #dee2e6;
  white-space: nowrap;
}

.summary-table td {
  padding: 10px 8px;
  border: 1px solid #dee2e6;
  vertical-align: middle;
}

.summary-table .category-cell {
  background-color: #fafbfc;
  font-weight: bold;
  color: #1e293b;
  text-align: center;
  writing-mode: vertical-rl;
  min-width: 40px;
  letter-spacing: 4px;
}

.summary-table .subcategory-cell {
  font-weight: 500;
  color: #334155;
}

.summary-table .num-cell {
  text-align: center;
}

.summary-table .points-cell {
  color: #22c55e;
  font-weight: 600;
}

.summary-table .credits-cell {
  color: #4f8ef7;
  font-weight: 600;
}

.summary-table .note-cell {
  color: #64748b;
  font-size: 13px;
  max-width: 200px;
}

/* 2. 下層明細區 */
.records-section {
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.records-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.records-header h3 {
  color: #333;
  font-size: 20px;
  margin: 0;
}

.filters-row {
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
  padding: 15px;
  background-color: #f8fafc;
  border-radius: 8px;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label {
  font-weight: 600;
  color: #475569;
  font-size: 14px;
}

.filter-date {
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  color: #495057;
}

.date-separator {
  color: #94a3b8;
  font-weight: bold;
}

.filter-select {
  padding: 6px 12px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 14px;
  color: #495057;
  background-color: white;
  cursor: pointer;
}

.filter-select:hover,
.filter-date:hover {
  border-color: #007bff;
}

.filter-select:focus,
.filter-date:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.15);
}

.filter-reset {
  padding: 6px 16px;
  background-color: #64748b;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.filter-reset:hover {
  background-color: #475569;
}

.muted {
  color: #94a3b8;
}

/* 活動紀錄表格 */
.records-table {
  width: 100%;
  border-collapse: collapse;
}

.records-table th {
  background-color: #f8f9fa;
  padding: 12px;
  text-align: left;
  font-weight: 600;
  color: #495057;
  border-bottom: 2px solid #dee2e6;
  font-size: 14px;
}

.records-table td {
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  vertical-align: middle;
}

.records-table tr:hover {
  background-color: #f8f9fa;
}

.records-table .subcategory-cell {
  font-weight: 500;
  color: #334155;
  white-space: nowrap;
}

.records-table .points-cell {
  text-align: center;
  color: #22c55e;
  font-weight: 600;
}

.records-table .credits-cell {
  text-align: center;
  color: #4f8ef7;
  font-weight: 600;
}

.records-table .note-cell {
  color: #64748b;
  font-size: 13px;
}

.records-table small {
  color: #94a3b8;
  font-size: 12px;
}

.activity-name {
  font-weight: 500;
  color: #212529;
  max-width: 200px;
}

/* 心得狀態 */
.essay-status {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
}

.status-none {
  background-color: #f1f5f9;
  color: #64748b;
}

.status-pending {
  background-color: #ffebee;
  color: #c62828;
}

.status-reviewing {
  background-color: #fff8e1;
  color: #f57f17;
}

.status-passed {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.no-records {
  text-align: center;
  padding: 40px;
  color: #6c757d;
  font-size: 16px;
}

/* 響應式設計 */
@media (max-width: 1024px) {
  .summary-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .my-activities {
    padding: 15px;
  }

  .tabs {
    flex-direction: column;
  }

  .tab-btn {
    border-bottom: 1px solid #ddd;
    border-left: 3px solid transparent;
  }

  .tab-btn.active {
    border-left-color: #007bff;
    border-bottom-color: transparent;
  }

  .summary-section,
  .records-section {
    padding: 20px;
  }

  .records-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }

  .filters-row {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-group {
    flex-wrap: wrap;
  }

  .filter-select,
  .filter-date {
    flex: 1;
  }

  .table-container {
    overflow-x: auto;
  }

  .records-table {
    min-width: 900px;
  }
}
</style>